import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { GoogleMap, Marker, MarkerClusterer, InfoWindow } from "@react-google-maps/api";
import { faList, faRotateLeft, faFilter, faEarthAmericas } from "@fortawesome/free-solid-svg-icons";
import ParksStore from "../../storage/parksStore";
import parkIconEnabled from "../../assets/images/park-icon-enabled.png";
import parkIconDisabled from "../../assets/images/park-icon-disabled.png";
import ClusterMarker from "../../assets/images/cluster-marker.png";
import FabButton from "../../sub/components/FabButton";
import { isMobileOrMobileWeb } from "../../platform";

const containerStyle = {
  width: "100vw",
  height: "100vh",
};

const center = {
  lat: 36.75,
  lng: -76.035,
};

const clusterStyles = [
  {
    url: ClusterMarker,
    height: 50,
    width: 50,
    textColor: "#ffffff",
    textSize: 14,
  },
];

const isMobileWeb = isMobileOrMobileWeb();

/* Main ParksMapView component */
const ParksMapView = () => {
  const parks = ParksStore((state) => state.parks);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [isFocused, setIsFocused] = useState(false); // 👈 NEW: focus flag
  const [markerSize, setMarkerSize] = useState(null);
  const [orientation, setOrientation] = useState(
    window.innerWidth > window.innerHeight && isMobileWeb ? "landscape" : "portrait"
  );
  const satelliteView = ParksStore((state) => state.satelliteView);
  const toggleSatelliteView = ParksStore((state) => state.toggleSatelliteView);

  useEffect(() => {
    let rafId;
    const handleResize = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        setOrientation(window.innerWidth > window.innerHeight && isMobileWeb ? "landscape" : "portrait");
      });
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const mapOptions = {
    styles: [
      {
        featureType: "poi",
        stylers: [{ visibility: "off" }],
      },
    ],
    mapTypeId: satelliteView ? "satellite" : "roadmap",
    disableDefaultUI: true,
    minZoom: 11,
    maxZoom: 18,
  };

  const navigation = useNavigation();
  const mapRef = useRef(null);

  /* Handle marker click to show InfoWindow */
  const handleMarkerClick = (parkId) => {
    setActiveMarkerId(parkId);
  };

  /* Handle refresh button click to reset map view */
  const handleRefreshClick = () => {
    setActiveMarkerId(null);

    if (mapRef.current) {
      mapRef.current.setCenter(center);
      mapRef.current.setZoom(12);
    }
  };

  // 👇 Only load the map when screen is focused
  useFocusEffect(
    useCallback(() => {
      setIsFocused(true);
      // clear InfoWindow on focus change
      setActiveMarkerId(null);
      return () => setIsFocused(false);
    }, [])
  );

  /* Memoized markers to optimize performance */
  const memoizedMarkers = useMemo(() => {
    return (clusterer) => {
      const validParks = parks
        .map((park) => {
          if (!park.latlng || park.latlng.length !== 2) return null;

          const lat = parseFloat(park.latlng[0]);
          const lng = parseFloat(park.latlng[1]);

          if (isNaN(lat) || isNaN(lng)) return null;

          return {
            ...park,
            parsedLat: lat,
            parsedLng: lng,
            parkId: `${park.ID}-${park.title}-${lat}-${lng}`,
          };
        })
        .filter(Boolean);

      return validParks.map((park) => (
        <Marker
          key={park.parkId}
          position={{
            lat: park.parsedLat,
            lng: park.parsedLng,
          }}
          icon={{
            url: activeMarkerId === park.parkId ? parkIconEnabled : parkIconDisabled,
            scaledSize: markerSize,
          }}
          clusterer={clusterer}
          onClick={() => handleMarkerClick(park.parkId)}
          title={park.title}
        >
          {activeMarkerId === park.parkId && (
            <InfoWindow onCloseClick={() => setActiveMarkerId(null)}>
              <button
                onClick={() => navigation.navigate("ParkDetails", { park_name: park.title })}
                style={infoWindowContentStyle}
                aria-label={`View details for ${park.title}`}
              >
                <div style={{ fontSize: 18, fontWeight: "bold" }}>{park.title}</div>
                <div style={{ fontSize: 16, paddingTop: 8 }}>{park.address}</div>
              </button>
            </InfoWindow>
          )}
        </Marker>
      ));
    };
  }, [parks, activeMarkerId, navigation, markerSize]);

  return (
    <View style={styles.wrapper}>
      {/* 👇 Only render map when focused */}
      {isFocused && (
        <View style={styles.wrapper}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={11}
            options={mapOptions}
            onLoad={(map) => {
              mapRef.current = map;
              setMarkerSize(new window.google.maps.Size(65, 70));
            }}
          >
            {markerSize && (
              <MarkerClusterer options={{ styles: clusterStyles }}>
                {(clusterer) => memoizedMarkers(clusterer)}
              </MarkerClusterer>
            )}
          </GoogleMap>
          <View style={styles.fabContainerLeft}>
            <FabButton
              customStyle={satelliteView ? { backgroundColor: "#9e9e9e" } : {}}
              icon={faEarthAmericas}
              onPress={toggleSatelliteView}
              accessibilityLabel="Satellite View"
            />
          </View>
          <View
            style={
              orientation === "portrait"
                ? styles.fabContainerRight
                : { ...styles.fabContainerRight, ...styles.fabContainerRightLandscape }
            }
          >
            <FabButton
              customStyle={orientation === "portrait" && styles.fabButton}
              icon={faFilter}
              onPress={() => navigation.navigate("Filter")}
              accessibilityLabel="Filter Parks"
            />
            <FabButton
              customStyle={orientation === "portrait" && styles.fabButton}
              icon={faRotateLeft}
              onPress={handleRefreshClick}
              accessibilityLabel="Refresh Parks"
            />
            <FabButton
              icon={faList}
              onPress={() => navigation.navigate("ParksList")}
              accessibilityLabel="View Parks List"
            />
          </View>
        </View>
      )}
    </View>
  );
};

const infoWindowContentStyle = {
  maxWidth: 500,
  fontFamily: "arial, sans-serif",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  textAlign: "left",
  cursor: "pointer",
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  fabButton: {
    marginBottom: 8,
  },
  fabContainerRight: {
    position: "absolute",
    justifyContent: "space-between",
    bottom: 10,
    right: 10,
    elevation: 5,
  },
  fabContainerRightLandscape: {
    flexDirection: "row",
    gap: 10,
  },
  fabContainerLeft: {
    position: "absolute",
    justifyContent: "space-between",
    bottom: 10,
    left: 10,
    elevation: 5,
  },
});

export default ParksMapView;
